import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './AdminContact.css';
import { customDateFormatShort } from '../../config/utils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

const AdminContact = () => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(
                `${API_URL}/api/${API_VERSION}/admin/dashboard/contact-messages`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessages(response.data.messages || []);
        } catch (error) {
            console.error('Error fetching contact messages:', error);
            toast.error('Failed to load contact messages');
        } finally {
            setLoading(false);
        }
    };

    const openReplyModal = (message) => {
        setSelectedMessage(message);
        setReplyText(message.reply_comments || '');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedMessage(null);
        setReplyText('');
    };

    const handleSendReply = async (markClosed = false) => {
        if (!selectedMessage) return;
        setSubmitting(true);
        try {
            const token = localStorage.getItem('adminToken');
            await axios.put(
                `${API_URL}/api/${API_VERSION}/admin/dashboard/reply-contact-message`,
                {
                    id: selectedMessage.id,
                    reply_comments: replyText,
                    status: markClosed ? 'Closed' : selectedMessage.status
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Reply saved successfully');
            closeModal();
            fetchMessages();
        } catch (error) {
            console.error('Error saving reply:', error);
            toast.error('Failed to save reply');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredMessages = messages.filter((msg) => {
        const matchesFilter =
            filter === 'all' ||
            (filter === 'open' && msg.status === 'Open') ||
            (filter === 'closed' && msg.status === 'Closed');
        const term = searchTerm.toLowerCase();
        const matchesSearch =
            msg.full_name?.toLowerCase().includes(term) ||
            msg.email_address?.toLowerCase().includes(term) ||
            msg.company_name?.toLowerCase().includes(term) ||
            msg.subject?.toLowerCase().includes(term);
        return matchesFilter && matchesSearch;
    });

    if (loading) {
        return (
            <div className="admin-contact">
                <div className="loading">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Loading messages...</p>
                </div>
            </div>
        );
    }

    const totalCount = messages.length;
    const openCount = messages.filter((m) => m.status === 'Open').length;
    const closedCount = messages.filter((m) => m.status === 'Closed').length;

    return (
        <div className="admin-contact">
            <div className="page-header">
                <h1>Contact Messages</h1>
                <div className="header-stats">
                    <div className="stat-card">
                        <span className="stat-label">Total</span>
                        <span className="stat-value">{totalCount}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Open</span>
                        <span className="stat-value">{openCount}</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Closed</span>
                        <span className="stat-value">{closedCount}</span>
                    </div>
                </div>
            </div>

            <div className="filters-section">
                <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 min-w-[320px] focus-within:ring-2 focus-within:ring-slate-300">
                    <i className="fas fa-search text-slate-400 text-sm"></i>
                    <input
                        type="text"
                        placeholder="Search by name, email, company, or subject..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 border-none outline-none text-sm placeholder:text-slate-400"
                    />
                </div>
                <div className="filter-buttons">
                    <button
                        className={filter === 'all' ? 'active' : ''}
                        onClick={() => setFilter('all')}
                    >
                        All
                    </button>
                    <button
                        className={filter === 'open' ? 'active' : ''}
                        onClick={() => setFilter('open')}
                    >
                        Open
                    </button>
                    <button
                        className={filter === 'closed' ? 'active' : ''}
                        onClick={() => setFilter('closed')}
                    >
                        Closed
                    </button>
                </div>
            </div>

            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Company</th>
                            <th>Subject</th>
                            <th>Status</th>
                            <th>Received</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMessages.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="no-data">No messages found</td>
                            </tr>
                        ) : (
                            filteredMessages.map((msg) => (
                                <tr key={msg.id}>
                                    <td>
                                        <div className="user-info custom-user-info">
                                            <span>{msg.full_name}</span>
                                        </div>
                                    </td>
                                    <td>{msg.email_address}</td>
                                    <td>{msg.company_name || 'N/A'}</td>
                                    <td>{msg.subject}</td>
                                    <td>
                                        <span
                                            className={`status-badge ${msg.status === 'Open' ? 'active' : 'inactive'
                                                }`}
                                        >
                                            {msg.status}
                                        </span>
                                    </td>
                                    <td>{customDateFormatShort(msg.createdAt)}</td>
                                    <td>
                                        <div className="text-center">
                                            <button
                                                className="btn-view"
                                                onClick={() => openReplyModal(msg)}
                                                title="Reply"
                                            >
                                                <i className="fas fa-reply"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && selectedMessage && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
                    onClick={closeModal}
                >
                    <div
                        className="bg-white rounded-xl w-[90%] max-w-[520px] max-h-[85vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100" style={{ background: '#1A2436' }}>
                            <h2 className="m-0 text-lg font-semibold text-white">Message Details</h2>
                            <button
                                className="border-none bg-transparent text-base cursor-pointer text-gray-500 hover:text-gray-700"
                                onClick={closeModal}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        <div className="p-5">
                            <div className="flex justify-between gap-3 py-2 border-b border-gray-50 text-sm">
                                <span className="text-gray-500 font-medium">Name:</span>
                                <span>{selectedMessage.full_name}</span>
                            </div>
                            <div className="flex justify-between gap-3 py-2 border-b border-gray-50 text-sm">
                                <span className="text-gray-500 font-medium">Email:</span>
                                <span>{selectedMessage.email_address}</span>
                            </div>
                            <div className="flex justify-between gap-3 py-2 border-b border-gray-50 text-sm">
                                <span className="text-gray-500 font-medium">Company:</span>
                                <span>{selectedMessage.company_name || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between gap-3 py-2 border-b border-gray-50 text-sm">
                                <span className="text-gray-500 font-medium">Phone:</span>
                                <span>{selectedMessage.phone_number || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between gap-3 py-2 border-b border-gray-50 text-sm">
                                <span className="text-gray-500 font-medium">Subject:</span>
                                <span>{selectedMessage.subject}</span>
                            </div>
                            <div className="flex justify-between gap-3 py-2 border-b border-gray-50 text-sm">
                                <span className="text-gray-500 font-medium">Status:</span>
                                <span
                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${selectedMessage.status === 'Open'
                                            ? 'bg-green-100 text-white'
                                            : 'bg-gray-100 text-gray-500'
                                        }`}
                                >
                                    {selectedMessage.status}
                                </span>
                            </div>
                            <div className="flex justify-between gap-3 py-2 border-b border-gray-50 text-sm">
                                <span className="text-gray-500 font-medium">Received:</span>
                                <span>{new Date(selectedMessage.createdAt).toLocaleString()}</span>
                            </div>
                            <div className="flex flex-col gap-1.5 py-2 border-b border-gray-50 text-sm">
                                <span className="text-gray-500 font-medium">Message:</span>
                                <p className="m-0 bg-gray-50 rounded-lg px-3 py-2.5 text-sm whitespace-pre-wrap">
                                    {selectedMessage.message}
                                </p>
                            </div>

                            <div className="mt-4 flex flex-col gap-1.5">
                                <label
                                    htmlFor="reply-comments"
                                    className="text-[13px] font-semibold text-gray-700"
                                >
                                    Reply Comments
                                </label>
                                <textarea
                                    id="reply-comments"
                                    rows="4"
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="Type your reply here..."
                                    className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm resize-y font-[inherit] outline-none focus:border-gray-400"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2.5 px-5 py-4 border-t border-gray-100">
                            <button
                                disabled={submitting}
                                onClick={() => handleSendReply(false)}
                                className="border-none rounded-lg px-4 py-2 text-sm cursor-pointer bg-gray-100 text-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                Save Reply
                            </button>
                            <button
                                disabled={submitting}
                                onClick={() => handleSendReply(true)}
                                className="border-none rounded-lg px-4 py-2 text-sm cursor-pointer bg-gray-900 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                Save & Closed
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminContact;